/*
 * Copyright (c) 2023-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit, signal, ViewChild} from '@angular/core';
import {EsciComponent} from '../esci.component';
import {HttpEventType, HttpResponse} from '@angular/common/http';
import JSZip from 'jszip';
import {GMIdenticalConnectorResultsHolder} from '../../model/GMIdenticalConnectorResultsHolder.model';
import {ColDef, GridApi} from 'ag-grid-community';
import {IdenticalConnectorPair} from '../../model/IdenticalConnectorPair.model';
import {AgGridAngular} from 'ag-grid-angular';

@Component({
    selector: 'app-identical-connector',
    templateUrl: './identical-connector.component.html',
    styleUrls: ['./identical-connector.component.scss'],
    standalone: false
})
export class IdenticalConnectorComponent extends EsciComponent implements OnInit {

  static readonly DEFAULT_DISTANCE_MM = 400;
  static readonly UNSUPPORTED_FILE_MESSAGE =
    'Could not run the Identical Connector Tool. Select a .zip of Harness Extraction files or a single .xlsx.';

  harnessUploadHeader = 'Harness Extraction:';
  harnessUploadSubheader = '*Select 1 zip of Harness Extraction files or 1 .xlsx';
  harnessUploadAccept = '.zip,.xlsx';
  harnessZip: File;
  @ViewChild('gridRef') gridRef!: AgGridAngular;
  private gridApi!: GridApi;
  harnessZipBlob: Blob | null;
  progress = signal(0);
  permissionUrl = '/esciUI/identicalConnectorPermission';
  gmIdenticalConnectorResultsHolder = new GMIdenticalConnectorResultsHolder();
  harnessToCountMap = new Map<string, number>();
  rowData: IdenticalConnectorPair[] = [];
  readonly rowDataSignal = signal<IdenticalConnectorPair[]>([]);
  harnessFilters: string[] = [];
  private setRowData(rows: IdenticalConnectorPair[]): void {
    this.rowData = rows || [];
    this.rowDataSignal.set([...(this.rowData || [])]);
  }


  private setProgress(value: number): void {
    this.progress.set(value);
  }

  defaultColDef: ColDef = {
    floatingFilter: true
  };

  columnDefs: ColDef[] = [
    {headerName: 'Connector 1', field: 'connector1.componentName', resizable: true, filter: true},
    {headerName: 'Associated RPO', field: 'connector1.rpo', valueFormatter: this.arrayFormatter, resizable: true, filter: true},
    {headerName: 'Connector Description', field: 'connector1.connectorDescription', resizable: true, filter: true},
    {headerName: 'In Harness', field: 'connector1.inHarness', resizable: true, sortable: true, filter: true},
    {headerName: 'Connector 2', field: 'connector2.componentName', resizable: true, filter: true},
    {headerName: 'Associated RPO', field: 'connector2.rpo', valueFormatter: this.arrayFormatter, resizable: true, filter: true},
    {headerName: 'Connector Description', field: 'connector2.connectorDescription', resizable: true, filter: true},
    {headerName: 'In Harness', field: 'connector2.inHarness', resizable: true, sortable: true, filter: true},
    {headerName: 'Distance (mm)', field: 'distance', resizable: true, sortable: true, filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: 'lessThanOrEqual', // Set the default filter type to less than or equal
      }, sort: 'asc'},
    {headerName: 'Component Match', field: 'componentMatch', resizable: true, sortable: true, filter: true},
  ];

  ngOnInit(): void {
    this.permission.set(false);
    this.esciService.getPermissionAndFileSizeLimit(this);
  }

  arrayFormatter(params: any) {
    if (params.value instanceof Array) {
      return params.value.join('#');
    }
    return '';
  }

  populateGridData() {
    this.setRowData(this.gmIdenticalConnectorResultsHolder.allIdenticalConnectorPairs);
    const api = this.gridRef?.api;
    if (api && !api.isDestroyed()) {
      api.setGridOption('rowData', this.rowData);
      api.sizeColumnsToFit();
    }
  }

  static isAcceptedHarnessExtractionFile(file: File): boolean {
    const name = file?.name?.toLowerCase() || '';
    return name.endsWith('.zip') || name.endsWith('.xlsx');
  }

  static buildDefaultDistanceFilterModel(): Record<string, {filterType: string; type: string; filter: number}> {
    return {
      distance: {
        filterType: 'number',
        type: 'lessThanOrEqual',
        filter: IdenticalConnectorComponent.DEFAULT_DISTANCE_MM
      }
    };
  }

  onResultsGridReady(event: {api: GridApi}): void {
    if (event?.api && !event.api.isDestroyed()) {
      this.gridApi = event.api;
    }
    this.applyDefaultDistanceFilter();
  }

  applyDefaultDistanceFilter(): void {
    const api = this.gridRef?.api || this.gridApi;
    if (!api || api.isDestroyed()) {
      return;
    }
    api.setFilterModel(IdenticalConnectorComponent.buildDefaultDistanceFilterModel());
    api.onFilterChanged();
  }

  clearFilters() {
    const api = this.gridRef?.api;
    if (api && !api.isDestroyed()) {
      api.setFilterModel(null);
      api.onFilterChanged();
    }
  }

  async downloadReport(): Promise<void> {
    const activeGridApi = this.gridRef?.api;
    if (!activeGridApi) {
      this.messageService.processError('Could not download the report. Please try again later.');
      return;
    }

    let rowCount = 0;
    activeGridApi.forEachNodeAfterFilterAndSort(() => rowCount++);
    if (rowCount < 1) {
      this.messageService.processError('There are no rows to download. Adjust filters and try again.');
      return;
    }

    try {
      await this.excelService.exportGridDataAsExcelFile(activeGridApi, 'IdenticalConnectorReport');
      this.messageService.processSuccess('Report successfully generated. Please see the downloaded file.');
    } catch (err) {
      console.log(err);
      this.messageService.processError('Could not download the report. Please try again later.');
    }
  }

  applyQuickFilter(key: string) {
    const index = this.harnessFilters.indexOf(key);
    if (index !== -1) {
      this.harnessFilters.splice(index, 1);
    } else {
      if (this.harnessFilters.length === 2) {
        this.harnessFilters.shift();
      }
      this.harnessFilters.push(key);
    }
    // Filter our actual row data
    if (this.harnessFilters.length === 0) {
      this.setRowData(this.gmIdenticalConnectorResultsHolder.allIdenticalConnectorPairs);
    } else if (this.harnessFilters.length === 1) {
      this.setRowData(this.gmIdenticalConnectorResultsHolder.allIdenticalConnectorPairs.filter((t) =>
          t.connector1.inHarness === this.harnessFilters[0] || t.connector2.inHarness === this.harnessFilters[0]));
    } else {
      this.setRowData(this.gmIdenticalConnectorResultsHolder.allIdenticalConnectorPairs.filter((t) =>
          (t.connector1.inHarness === this.harnessFilters[0] && t.connector2.inHarness === this.harnessFilters[1]) ||
        (t.connector1.inHarness === this.harnessFilters[1] && t.connector2.inHarness === this.harnessFilters[0])));
    }
    this.gridRef.api?.setGridOption('rowData', this.rowData);
  }

  populateHarnessToCountMap() {
    this.harnessToCountMap.clear();
    // Take the allIdenticalConnectorPairs and populate our map of harness names to count
    // First, gather all harness names from both connector 1 and 2 in our list of pairs
    const conn1Harns = this.gmIdenticalConnectorResultsHolder.allIdenticalConnectorPairs.map((t) => t.connector1.inHarness);
    const conn2Harns = this.gmIdenticalConnectorResultsHolder.allIdenticalConnectorPairs.map((t) => t.connector2.inHarness);
    const allHarnArray = conn1Harns.concat(conn2Harns);
    allHarnArray.sort((a, b) => a.localeCompare(b));
    const allHarnesses = new Set(allHarnArray);
    for (const harness of allHarnesses) {
      const count = this.gmIdenticalConnectorResultsHolder.allIdenticalConnectorPairs.filter((t) =>
          t.connector1.inHarness === harness || t.connector2.inHarness === harness).length;
      this.harnessToCountMap.set(harness, count);
    }
  }

  generateIdenticalConnectors() {
    if (this.harnessZip?.size) {
      if (!IdenticalConnectorComponent.isAcceptedHarnessExtractionFile(this.harnessZip)) {
        this.messageService.processError(IdenticalConnectorComponent.UNSUPPORTED_FILE_MESSAGE);
        return;
      }
      this.setProgress(10);
      this.gmIdenticalConnectorResultsHolder = new GMIdenticalConnectorResultsHolder();
      this.harnessToCountMap = new Map<string, number>();
      const zip = new JSZip();
      zip.file(this.harnessZip.name, this.harnessZip);
      zip.generateAsync({type: 'blob'}).then((content) => {
        this.harnessZipBlob = content;
        this.uploadService.uploadFilesForIdenticalConnector(this.harnessZipBlob).subscribe({
            next: (event: any) => {
              if (event.type === HttpEventType.UploadProgress) {
                this.setProgress(Math.round(100 * event.loaded / event.total));
              } else if (event instanceof HttpResponse) {
                this.setProgress(0);
                if (event.body) {
                  const responseData = event.body;
                  if (responseData.allIdenticalConnectorPairs) {
                    this.gmIdenticalConnectorResultsHolder.allIdenticalConnectorPairs = responseData.allIdenticalConnectorPairs;
                    this.gmIdenticalConnectorResultsHolder.resultCode = responseData.resultCode;
                    this.populateHarnessToCountMap();
                  }
                  this.populateGridData();
                  this.messageService.processSuccess('Identical Connector Tool completed successfully.');
                } else {
                  this.messageService.processError('Could not run the Identical Connector Tool. Please check the file provided and try again later.');
                }
              }
            },
            error: (err: any) => {
              this.setProgress(0);
              console.log(err);
              this.messageService.processError('Could not run the Identical Connector Tool. Please check the file provided and try again later.');
            }
          });
      });
    }
  }
}
